import React from 'react';
import { CartesianGrid, ResponsiveContainer, Scatter, ScatterChart, XAxis, YAxis } from 'recharts';

import { ReportingPeriod, StatisticsView, StatisticsWindow } from '@ndvr/reporting-api';

import { Card } from '@/components/ui/card';
import { cn } from '@/utils/cn';

import { toNumber } from '../../reporting/format';
import { GlossaryLabel } from '../../reporting/GlossaryInfoButton';
import { HEAD_CLASS, TABLE_CARD_HEADER_CLASS, TABLE_CLASS } from '../../reporting/table-styles';
import {
    NAV_CHART_AXIS_TICK_COLOR,
    NAV_CHART_AXIS_TICK_FONT_SIZE,
    NAV_CHART_GRID_COLOR,
    NAV_CHART_LINE_COLOR,
} from '../constants';
import { periodLabel, RISK_MEASUREMENT_ROWS } from '../statistics-format';

interface RiskMeasurementsCardProps {
    view: StatisticsView;
    // The benchmark's chart line color, so its scatter point matches.
    benchmarkColor: string;
    accountLabel: string;
}

interface ScatterPoint {
    label: string;
    risk: number;
    ret: number;
    color: string;
}

// One point per series from the window's already-computed statistics: x = annualized
// standard deviation, y = the window's return. Series with a missing figure are dropped.
const scatterPoints = (
    window: StatisticsWindow,
    view: StatisticsView,
    benchmarkColor: string,
    accountLabel: string,
): ScatterPoint[] => {
    const points: Array<{ label: string; color: string; risk: number | null; ret: number | null }> = [
        {
            label: accountLabel,
            color: NAV_CHART_LINE_COLOR,
            risk: toNumber(window.accountRiskMetrics?.standardDeviation),
            ret: toNumber(window.accountRiskMetrics?.returnPercent),
        },
        {
            label: view.benchmarkName,
            color: benchmarkColor,
            risk: toNumber(window.benchmarkRiskMetrics?.standardDeviation),
            ret: toNumber(window.benchmarkRiskMetrics?.returnPercent),
        },
    ];
    return points.filter((p): p is ScatterPoint => p.risk !== null && p.ret !== null);
};

// Whole-percent axis bounds with breathing room so no point sits on a chart edge.
const paddedDomain = (values: number[], pad: number): [number, number] => [
    Math.floor(Math.min(...values) - pad),
    Math.ceil(Math.max(...values) + pad),
];

const percentTick = (value: number) => `${value}%`;

// Dot plus its label in one marker so the label can anchor away from the nearer side
// edge (recharts' own label list wraps long names at the chart boundary instead).
interface PointMarkerProps {
    cx?: number;
    cy?: number;
    point: ScatterPoint;
    anchorEnd: boolean;
}

const PointMarker = ({ cx, cy, point, anchorEnd }: PointMarkerProps) => {
    if (cx === undefined || cy === undefined) {
        return null;
    }
    return (
        <g>
            <circle cx={cx} cy={cy} r={6.5} fill={point.color} stroke="#fff" strokeWidth={2.5} />
            <text
                x={anchorEnd ? cx - 13 : cx + 13}
                y={cy + 4}
                textAnchor={anchorEnd ? 'end' : 'start'}
                fontSize={12.5}
                fontWeight={600}
                fill="#2C2424"
            >
                {point.label}
            </text>
        </g>
    );
};

const RiskReturnScatter = ({ points }: { points: ScatterPoint[] }) => {
    const xDomain = paddedDomain(
        points.map((p) => p.risk),
        1.5,
    );
    // Extra headroom on the y-axis keeps labels clear of the top edge.
    const yDomain = paddedDomain(
        points.map((p) => p.ret),
        2,
    );
    // Labels flip to the left of the dot in the right half of the plot so long benchmark
    // names extend inward instead of clipping at the chart edge.
    const xMidpoint = (xDomain[0] + xDomain[1]) / 2;

    return (
        <div className="w-full max-w-3xl">
            {/* Numeric height: percentage heights are measured before the parent lays out
                and trigger recharts' "width(-1) and height(-1)" warning. */}
            <ResponsiveContainer width="100%" height={320}>
                <ScatterChart margin={{ top: 12, right: 24, bottom: 24, left: 8 }}>
                    <CartesianGrid stroke={NAV_CHART_GRID_COLOR} strokeWidth={0.5} />
                    <XAxis
                        type="number"
                        dataKey="risk"
                        domain={xDomain}
                        tickCount={xDomain[1] - xDomain[0] + 1}
                        allowDecimals={false}
                        tickFormatter={percentTick}
                        tick={{ fontSize: NAV_CHART_AXIS_TICK_FONT_SIZE, fill: NAV_CHART_AXIS_TICK_COLOR }}
                        tickLine={false}
                        axisLine={{ stroke: NAV_CHART_GRID_COLOR }}
                        label={{
                            value: 'Risk (standard deviation)',
                            position: 'insideBottom',
                            offset: -16,
                            fontSize: 12,
                            fill: NAV_CHART_AXIS_TICK_COLOR,
                        }}
                    />
                    <YAxis
                        type="number"
                        dataKey="ret"
                        domain={yDomain}
                        allowDecimals={false}
                        tickFormatter={percentTick}
                        tick={{ fontSize: NAV_CHART_AXIS_TICK_FONT_SIZE, fill: NAV_CHART_AXIS_TICK_COLOR }}
                        tickLine={false}
                        axisLine={{ stroke: NAV_CHART_GRID_COLOR }}
                        label={{
                            value: 'Return',
                            angle: -90,
                            position: 'insideLeft',
                            fontSize: 12,
                            fill: NAV_CHART_AXIS_TICK_COLOR,
                        }}
                    />
                    {points.map((point) => (
                        <Scatter
                            key={point.label}
                            data={[point]}
                            isAnimationActive={false}
                            shape={(props: { cx?: number; cy?: number }) => (
                                <PointMarker
                                    cx={props.cx}
                                    cy={props.cy}
                                    point={point}
                                    anchorEnd={point.risk > xMidpoint}
                                />
                            )}
                        />
                    ))}
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
};

// Risk measurements of the account and its benchmark per trailing window, with the 1-year
// window's risk/return positioning plotted beneath — the layout of the "Risk measurements"
// card in the Advanced Analytics design.
export const RiskMeasurementsCard = ({ view, benchmarkColor, accountLabel }: RiskMeasurementsCardProps) => {
    const scatterWindow = view.windows.find((w) => w.period === ReportingPeriod.ONE_YEAR) ?? view.windows[0];
    const points = scatterWindow ? scatterPoints(scatterWindow, view, benchmarkColor, accountLabel) : [];

    return (
        <Card className="border-border overflow-hidden rounded-2xl border bg-white">
            <div className={cn(TABLE_CARD_HEADER_CLASS, 'border-b')}>
                <h3 className="font-heading text-cocoa-1000 text-xl">Risk measurements</h3>
                <span className="text-cocoa-400 text-xs">Account vs. {view.benchmarkName}</span>
            </div>
            <table className={cn('w-full', TABLE_CLASS)}>
                <thead>
                    <tr className="border-b">
                        <th className="w-[22%] px-4 py-2.5" />
                        {view.windows.map((window) => (
                            <th key={window.period} colSpan={2} className={`${HEAD_CLASS} px-4 py-2.5 text-center`}>
                                {periodLabel(window.period)}
                            </th>
                        ))}
                    </tr>
                    <tr className="border-b">
                        <th className="px-4 py-1.5" />
                        {view.windows.map((window) => (
                            <React.Fragment key={window.period}>
                                <th className={`${HEAD_CLASS} px-4 py-1.5 text-right`}>Account</th>
                                <th className={`${HEAD_CLASS} px-4 py-1.5 text-right`}>Bench.</th>
                            </React.Fragment>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {RISK_MEASUREMENT_ROWS.map(({ label, field, format, glossaryKey }) => (
                        <tr key={label} className="border-b last:border-b-0">
                            <td className="text-cocoa-900 px-4 py-3 text-sm">
                                <GlossaryLabel label={label} glossaryKey={glossaryKey} />
                            </td>
                            {view.windows.map((window) => (
                                <React.Fragment key={window.period}>
                                    <td className="text-cocoa-900 px-4 py-3 text-right text-sm font-medium tabular-nums">
                                        {format(window.accountRiskMetrics?.[field])}
                                    </td>
                                    <td className="text-cocoa-400 px-4 py-3 text-right text-sm tabular-nums">
                                        {format(window.benchmarkRiskMetrics?.[field])}
                                    </td>
                                </React.Fragment>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {points.length > 0 && scatterWindow && (
                <div className="border-t p-4 lg:p-6">
                    <div className="text-cocoa-900 mb-1 text-sm font-semibold">Risk vs. return</div>
                    <div className="text-cocoa-400 mb-2 text-xs">
                        {periodLabel(scatterWindow.period)} period · risk measured as standard deviation
                    </div>
                    <RiskReturnScatter points={points} />
                </div>
            )}
        </Card>
    );
};
